"use client";

import React from "react";
import PageBreadCrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import DataTable from "@/components/ui/DataTable";
import SearchableSelect from "@/components/ui/SearchableSelect";
import DatePicker from "@/components/form/date-picker";
import Pagination from "@/components/tables/Pagination";
import DrawerPanel from "@/components/ui/DrawerPanel";
import Input from "@/components/form/input/InputField";
import Label from "@/components/form/Label";
import { Modal } from "@/components/ui/modal/index";
import { PencilIcon, TrashBinIcon } from "@/icons";
import { handleApiError } from "@/lib/api";
import { rmcClient } from "@/lib/rmcClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

const emptyForm = {
  purchase_party_id: "",
  date: new Date().toISOString().slice(0, 10),
  vehicle_no: "",
  material_name: "",
  amount: "",
  delivery_by: "",
  gst_rate: "",
  remark: "",
};

const PAGE_SIZE = 20;

export default function RmcMiscExpenses() {
  const qc = useQueryClient();
  const isoToday = React.useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, []);
  const [filters, setFilters] = React.useState({
    date_from: isoToday,
    date_to: isoToday,
    purchase_party_id: "",
  });
  const [page, setPage] = React.useState(1);
  const [isExporting, setIsExporting] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [form, setForm] = React.useState(emptyForm);
  const [deleteRow, setDeleteRow] = React.useState(null);

  const { data: purchaseParties = [] } = useQuery({
    queryKey: ["purchaseParties"],
    queryFn: async () => (await rmcClient.listPurchaseParties()).data,
  });
  const queryParams = React.useMemo(
    () => ({
      ...(filters.purchase_party_id ? { purchase_party_id: filters.purchase_party_id } : {}),
      ...(filters.date_from ? { date_from: filters.date_from } : {}),
      ...(filters.date_to ? { date_to: filters.date_to } : {}),
      page,
      page_size: PAGE_SIZE,
    }),
    [filters, page]
  );
  const { data: miscExpensesData, isFetching } = useQuery({
    queryKey: ["miscExpenses", queryParams],
    queryFn: async () => (await rmcClient.listMiscExpenses(queryParams)).data,
    keepPreviousData: true,
  });
  const rows = React.useMemo(() => {
    if (Array.isArray(miscExpensesData)) return miscExpensesData;
    if (Array.isArray(miscExpensesData?.results)) return miscExpensesData.results;
    if (Array.isArray(miscExpensesData?.data?.results)) return miscExpensesData.data.results;
    return [];
  }, [miscExpensesData]);
  const totalCount = React.useMemo(() => {
    const value = Number(miscExpensesData?.count);
    if (Number.isFinite(value) && value >= 0) return value;
    const nested = Number(miscExpensesData?.data?.count);
    if (Number.isFinite(nested) && nested >= 0) return nested;
    return rows.length;
  }, [miscExpensesData, rows.length]);
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const activeFilterCount = React.useMemo(
    () => [filters.date_from, filters.date_to, filters.purchase_party_id].filter(Boolean).length,
    [filters]
  );

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, date: new Date().toISOString().slice(0, 10) });
    setModalOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      purchase_party_id: r.purchase_party_id ?? "",
      date: r.date || "",
      vehicle_no: r.vehicle_no || "",
      material_name: r.material_name || "",
      amount: r.base_amount ?? r.amount ?? "",
      delivery_by: r.delivery_by || "",
      gst_rate: r.gst_rate ?? "",
      remark: r.remark || "",
    });
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setForm(emptyForm);
  };

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        purchase_party_id: form.purchase_party_id ? Number(form.purchase_party_id) : undefined,
        date: form.date,
        vehicle_no: form.vehicle_no || "",
        material_name: form.material_name || "",
        amount: form.amount,
        delivery_by: form.delivery_by || "",
        remark: form.remark || "",
      };
      if (form.gst_rate !== "") payload.gst_rate = form.gst_rate;
      if (editing?.id) return rmcClient.updateMiscExpense(editing.id, payload);
      return rmcClient.createMiscExpense(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["miscExpenses"] });
      toast.success(editing?.id ? "Expense updated" : "Expense created");
      closeModal();
    },
    onError: (e) => toast.error(handleApiError(e)),
  });

  const remove = useMutation({
    mutationFn: (id) => rmcClient.deleteMiscExpense(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["miscExpenses"] });
      toast.success("Expense deleted");
      setDeleteRow(null);
    },
    onError: (e) => toast.error(handleApiError(e)),
  });

  const partyName = React.useCallback(
    (id) => purchaseParties.find((p) => p.id === id)?.name || "—",
    [purchaseParties]
  );
  const updateFilters = React.useCallback((next) => {
    setFilters((prev) => ({ ...prev, ...next }));
    setPage(1);
  }, []);
  const clearFilters = React.useCallback(() => {
    setFilters({
      date_from: isoToday,
      date_to: isoToday,
      purchase_party_id: "",
    });
    setPage(1);
  }, [isoToday]);
  const exportFiltered = React.useCallback(async () => {
    setIsExporting(true);
    try {
      const payload = (
        await rmcClient.listMiscExpenses({
          ...(filters.purchase_party_id ? { purchase_party_id: filters.purchase_party_id } : {}),
          ...(filters.date_from ? { date_from: filters.date_from } : {}),
          ...(filters.date_to ? { date_to: filters.date_to } : {}),
          pagination: false,
        })
      ).data;
      const exportRows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.results)
          ? payload.results
          : Array.isArray(payload?.data?.results)
            ? payload.data.results
            : [];
      const header = [
        "date",
        "purchase_party",
        "vehicle_no",
        "material_name",
        "base_amount",
        "gst_rate",
        "total_amount",
        "delivery_by",
        "remark",
      ];
      const lines = exportRows.map((r) =>
        [
          r.date ?? "",
          partyName(r.purchase_party_id),
          r.vehicle_no ?? "",
          r.material_name ?? "",
          r.base_amount ?? r.amount ?? "",
          r.gst_rate ?? "",
          r.total_amount ?? "",
          r.delivery_by ?? "",
          r.remark ?? "",
        ]
          .map((v) => `"${String(v ?? "").replaceAll('"', '""')}"`)
          .join(",")
      );
      const csv = [header.join(","), ...lines].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `misc-expenses-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${exportRows.length} expenses`);
    } catch {
      toast.error("Failed to export expenses.");
    } finally {
      setIsExporting(false);
    }
  }, [filters, partyName]);
  const baseAmount = Number(form.amount || 0);
  const gstPct = Number(form.gst_rate || 0);
  const gstAmount = Number.isFinite(baseAmount) && Number.isFinite(gstPct)
    ? (baseAmount * gstPct) / 100
    : 0;
  const totalWithGst = (Number.isFinite(baseAmount) ? baseAmount : 0) + (Number.isFinite(gstAmount) ? gstAmount : 0);
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <PageBreadCrumb pageTitle="Miscellaneous Expenses" />
        <div className="flex items-end gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={isExporting}
            onClick={exportFiltered}
          >
            {isExporting ? "Exporting…" : "Export All Filtered"}
          </Button>
          <Button size="sm" onClick={openCreate}>
            Add Expense
          </Button>
        </div>
      </div>
      <div className="rounded-2xl border border-gray-200/80 bg-white/90 p-4 shadow-lg shadow-gray-200/50 ring-1 ring-gray-100/80 backdrop-blur-sm transition-all dark:border-gray-800 dark:bg-gray-900/50 dark:shadow-black/30 dark:ring-gray-800/80">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
              Smart Filters
            </p>
            {activeFilterCount > 0 && (
              <span className="menu-dropdown-badge-active rounded-full px-2.5 py-1 text-[11px] font-semibold">
                {activeFilterCount} active
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Showing {rows.length} of {totalCount} records
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Date From</label>
            <DatePicker
              id="misc-expense-filter-from"
              defaultDate={filters.date_from}
              placeholder="From date"
              onChange={(_, dateStr) => updateFilters({ date_from: dateStr || "" })}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Date To</label>
            <DatePicker
              id="misc-expense-filter-to"
              defaultDate={filters.date_to}
              placeholder="To date"
              onChange={(_, dateStr) => updateFilters({ date_to: dateStr || "" })}
            />
          </div>
          <SearchableSelect
            label="Purchase Party"
            value={filters.purchase_party_id}
            onChange={(v) => updateFilters({ purchase_party_id: v || "" })}
            options={[{ id: "", name: "All parties" }, ...purchaseParties]}
            getLabel={(o) => o.name}
            getValue={(o) => o.id}
            getSubLabel={(p) => p.gst_number}
            placeholder="All parties"
            overlayZClass="z-[100001]"
            menuZClass="z-[100002]"
          />
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-gray-200/70 pt-3 dark:border-gray-800">
          <Button type="button" size="sm" variant="outline" onClick={clearFilters}>
            Clear Filters
          </Button>
        </div>
      </div>

      <DataTable
        columns={[
          { key: "date", header: "Date" },
          { key: "party", header: "Purchase Party", render: (r) => partyName(r.purchase_party_id) },
          { key: "vehicle_no", header: "Vehicle", render: (r) => r.vehicle_no || "—" },
          { key: "material", header: "Material", render: (r) => r.material_name || "—" },
          {
            key: "amount",
            header: "Amount",
            render: (r) => `₹${Number(r.total_amount ?? r.base_amount ?? r.amount ?? 0).toFixed(2)}`,
          },
          { key: "delivery_by", header: "Delivery By", render: (r) => r.delivery_by || "—" },
          { key: "gst", header: "GST %", render: (r) => (r.gst_rate != null ? Number(r.gst_rate).toFixed(2) : "—") },
          { key: "remark", header: "Remark", render: (r) => r.remark || "—" },
          {
            key: "actions",
            header: "",
            headerClassName: "text-right",
            cellClassName: "text-right",
            render: (r) => (
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:border-gray-700 dark:bg-transparent dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-white"
                  onClick={(e) => {
                    e.stopPropagation();
                    openEdit(r);
                  }}
                >
                  <PencilIcon className="size-4" />
                </button>
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-red-50 hover:text-red-600 dark:border-gray-700 dark:bg-transparent dark:text-gray-300 dark:hover:bg-red-500/10 dark:hover:text-red-400"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteRow(r);
                  }}
                >
                  <TrashBinIcon className="size-4" />
                </button>
              </div>
            ),
          },
        ]}
        data={rows}
        isLoading={isFetching}
      />
      <div className="flex justify-end">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(p) => setPage(Math.max(1, Math.min(totalPages, p)))}
        />
      </div>

      <DrawerPanel
        open={modalOpen}
        onClose={closeModal}
        title={editing?.id ? "Edit Misc Expense" : "Add Misc Expense"}
        widthClass="max-w-3xl"
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <SearchableSelect
            label="Purchase Party"
            value={form.purchase_party_id}
            onChange={(v) => setForm((p) => ({ ...p, purchase_party_id: v || "" }))}
            options={purchaseParties}
            getSubLabel={(p) => p.gst_number}
            overlayZClass="z-[100002]"
            menuZClass="z-[100003]"
          />
          <div>
            <Label>Date</Label>
            <Input type="date" className="mt-1" value={form.date} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} />
          </div>
          <div>
            <Label>Vehicle No</Label>
            <Input className="mt-1" value={form.vehicle_no} onChange={(e) => setForm((p) => ({ ...p, vehicle_no: e.target.value }))} />
          </div>
          <div>
            <Label>Material</Label>
            <Input
              className="mt-1"
              value={form.material_name}
              onChange={(e) => setForm((p) => ({ ...p, material_name: e.target.value }))}
            />
          </div>
          <div>
            <Label>Amount</Label>
            <Input type="number" step="0.01" className="mt-1" value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} />
          </div>
          <div>
            <Label>Delivery By</Label>
            <Input className="mt-1" value={form.delivery_by} onChange={(e) => setForm((p) => ({ ...p, delivery_by: e.target.value }))} />
          </div>
          <div>
            <Label>GST Rate %</Label>
            <Input type="number" step="0.01" className="mt-1" value={form.gst_rate} onChange={(e) => setForm((p) => ({ ...p, gst_rate: e.target.value }))} />
          </div>
          <div className="sm:col-span-2">
            <Label>Remark</Label>
            <Input className="mt-1" value={form.remark} onChange={(e) => setForm((p) => ({ ...p, remark: e.target.value }))} />
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={closeModal}>
            Cancel
          </Button>
          <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending || !form.material_name || !form.amount || !form.date}>
            {save.isPending ? "Saving..." : editing?.id ? "Update" : "Save"}
          </Button>
        </div>
        <div className="mt-4 rounded-lg border border-gray-800 bg-gray-950/40 p-3">
          <p className="text-[11px] uppercase tracking-wide text-gray-500">Confirmation</p>
          <div className="mt-2 space-y-1 text-sm">
            <p className="text-gray-300">
              Base Amount: <span className="font-semibold text-white">₹ {baseAmount.toFixed(2)}</span>
            </p>
            <p className="text-gray-300">
              GST ({gstPct.toFixed(2)}%): <span className="font-semibold text-white">₹ {gstAmount.toFixed(2)}</span>
            </p>
            <p className="text-gray-200">
              Total: <span className="font-semibold text-white">₹ {totalWithGst.toFixed(2)}</span>
            </p>
          </div>
        </div>
      </DrawerPanel>

      <Modal isOpen={deleteRow != null} onClose={() => !remove.isPending && setDeleteRow(null)} className="max-w-md w-full">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">Delete expense?</h3>
          <p className="text-sm text-gray-400">This will permanently delete the selected miscellaneous expense.</p>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" disabled={remove.isPending} onClick={() => setDeleteRow(null)}>Cancel</Button>
            <Button size="sm" disabled={remove.isPending} onClick={() => deleteRow?.id && remove.mutate(deleteRow.id)}>
              {remove.isPending ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
